/*
 * SmartReserve Batterijcheck rekenmodel  (versie 2026-09-28 · "BC-2026.09")
 * Eén gedeeld model voor de Batterijcheck (bespaarcalculator), de matchtest en het adviesdocument (Python-port).
 * Dezelfde invoer geeft overal dezelfde uitkomst.
 *
 * Uitgangspunten (zie ook /pages/bespaar-calculator):
 *  - We vergelijken de energiekosten mét en zonder batterij voor dezelfde situatie, per jaar vanaf 1-1-2027
 *    (na het einde van salderen). Tot die datum levert opslag van zonnestroom vrijwel niets op.
 *  - Vermogen: 3.000 W per set (laden en ontladen), de bestaande rekenbasis. Per set = 1 toren.
 *  - Bruikbare opslag: HYXI publiceert geen bruikbaar percentage; wij rekenen met 90% van de nominale kWh.
 *  - Rendement heen en terug: 85% (laag scenario) tot 90% (hoog scenario); verschilt per huishouden (keuze SmartReserve 28-9).
 *  - Stand-by: 9 W per set, het hele jaar (± 79 kWh per set per jaar), tegen het inkooptarief.
 *  - Slijtage: gemiddeld 93% van de capaciteit over 10 jaar (± 1,5% per jaar), toegepast op alle opbrengsten.
 *  - Zon: ± 360 kWh per paneel per jaar (400 Wp × 0,9 kWh/Wp) tenzij je eigen opwek of teruglevering invult.
 *    Maandverdeling zon en verbruik volgens een gedocumenteerd gemiddeld NL-profiel (geen uurdata).
 *  - Dagelijkse schommeling (bewolkte dagen) via een benuttingsfactor 75% (laag) tot 90% (hoog).
 *  - Vast/variabel contract: alleen zonnestroom verschuiven. Dynamisch contract: daarnaast de nog lege
 *    capaciteit op goedkope uren laden en op dure uren zelf gebruiken (geen terugleveren voor winst, geen handel).
 *    Prijsverschil goedkoop/duur € 0,08 tot € 0,12 per kWh, op 150 tot 250 dagen per jaar.
 *  - Dezelfde kWh wordt nooit twee keer geteld: netladen gebruikt alleen capaciteit die de zon niet vulde.
 *  - Terugleverkosten per kWh (optioneel) verlagen de waarde van teruggeleverde stroom; vaste kosten veranderen niet.
 *  - Geen onbalans-/VPP-opbrengst, geen btw-teruggave, geen CO2-bedrag.
 */
(function (root) {
  'use strict';

  var VERSION = 'BC-2026.09';
  var PEILDATUM = '28-09-2026';

  var PV_MONTH = [0.022, 0.042, 0.080, 0.115, 0.135, 0.140, 0.135, 0.115, 0.090, 0.060, 0.028, 0.018];
  var USE_MONTH = [0.100, 0.090, 0.090, 0.080, 0.075, 0.070, 0.070, 0.070, 0.075, 0.085, 0.095, 0.100];
  var DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  (function normalise() {
    var s = PV_MONTH.reduce(function (a, b) { return a + b; }, 0);
    for (var i = 0; i < 12; i++) PV_MONTH[i] = PV_MONTH[i] / s;
  })();

  var TARIFFS = {
    // inkoop = all-in per kWh incl. energiebelasting en btw (CBS-niveau 2026); vergoeding = wat je na 2027
    // naar verwachting krijgt voor teruggeleverde stroom (minimaal 50% van het kale tarief t/m 2030).
    vast:      { label: 'vast',      buy: 0.27, sell: 0.06 },
    variabel:  { label: 'variabel',  buy: 0.26, sell: 0.05 },
    dynamisch: { label: 'dynamisch', buy: 0.26, sell: 0.04, low: 0.21, spreadLow: 0.08, spreadHigh: 0.12 }
  };

  var PROFILES = {
    // aandeel van het dagverbruik dat tijdens zonne-uren valt
    avond:     { label: "vooral 's avonds", dayShare: 0.30 },
    onbekend:  { label: 'weet ik niet',     dayShare: 0.38 },
    verspreid: { label: 'verspreid over de dag', dayShare: 0.45 }
  };

  var C = {
    KWH_PER_PANEL: 360,
    USABLE: 0.90,
    ETA_LOW: 0.85, ETA_HIGH: 0.90,
    UTIL_LOW: 0.75, UTIL_HIGH: 0.90,
    MATCH: 0.80,              // hoe goed zonopwek en dagverbruik samenvallen (direct eigen verbruik)
    STANDBY_W: 9,
    DEGRADATION: 0.93,
    SET_KW: 3.0,              // 3.000 W per set, laden en ontladen
    SUN_HOURS_LOW: 5, SUN_HOURS_HIGH: 6,
    DISCHARGE_HOURS: 8,
    DYN_DAYS_LOW: 150, DYN_DAYS_HIGH: 250,
    HORIZON: 10,
    MARGINAL_PAYBACK_MAX: 10  // extra capaciteit alleen adviseren als die zich binnen 10 jaar terugverdient
  };

  // Standaard pakketprijzen (incl. btw). De pagina overschrijft deze met de live variantprijzen uit Shopify.
  var DEFAULT_PRICES = { 6: 1399, 9: 1939, 12: 2550, 15: 3170, 18: 3770 };

  function num(v, d) { var n = parseFloat(String(v).replace(',', '.')); return isFinite(n) ? n : d; }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  /* Normaliseer en valideer invoer. Retourneert { input, warnings } */
  function normalise(raw) {
    raw = raw || {};
    var w = [];
    var contract = TARIFFS[raw.contract] ? raw.contract : 'vast';
    if (!TARIFFS[raw.contract]) w.push('Contract onbekend: we rekenen met een vast contract (voorzichtigste uitkomst).');
    var profile = PROFILES[raw.profile] ? raw.profile : 'onbekend';
    var usage = num(raw.usageKwh, 3500);
    if (usage < 500 || usage > 60000) { w.push('Jaarverbruik buiten bereik aangepast (500 tot 60.000 kWh).'); }
    usage = clamp(usage, 500, 60000);
    var usageType = raw.usageType === 'totaal' ? 'totaal' : 'afname';
    var panels = Math.round(num(raw.panels, 0));
    if (panels < 0 || panels > 200) w.push('Aantal panelen aangepast (0 tot 200).');
    panels = clamp(panels, 0, 200);
    var pvKwh = raw.pvKwh != null && raw.pvKwh !== '' ? clamp(num(raw.pvKwh, 0), 0, 200000) : null;
    var exportKwh = raw.exportKwh != null && raw.exportKwh !== '' ? clamp(num(raw.exportKwh, 0), 0, 200000) : null;
    var t = TARIFFS[contract];
    var buy = raw.buy != null && raw.buy !== '' ? clamp(num(raw.buy, t.buy), 0.05, 1.0) : t.buy;
    var sell = raw.sell != null && raw.sell !== '' ? clamp(num(raw.sell, t.sell), -0.2, 0.5) : t.sell;
    var feedCost = raw.feedCost != null && raw.feedCost !== '' ? clamp(num(raw.feedCost, 0), 0, 0.5) : 0;
    var estimatedPv = pvKwh == null;
    if (pvKwh == null) pvKwh = panels * C.KWH_PER_PANEL;
    return {
      input: { contract: contract, profile: profile, usageKwh: usage, usageType: usageType, panels: panels,
               pvKwh: pvKwh, pvEstimated: estimatedPv, exportKwh: exportKwh, buy: buy, sell: sell, feedCost: feedCost },
      warnings: w
    };
  }

  /* Maandprofiel van het huishouden: verbruik, zon, direct eigen verbruik, overschot, avond/nachtbehoefte */
  function household(inp) {
    var profile = PROFILES[inp.profile];
    var pv = inp.pvKwh;
    // Direct eigen verbruik per maand (zonder batterij)
    function directFor(totalUse) {
      var months = [];
      for (var m = 0; m < 12; m++) {
        var d = DAYS[m];
        var pvD = pv * PV_MONTH[m] / d;
        var useD = totalUse * USE_MONTH[m] / d;
        var dayUse = useD * profile.dayShare;
        var direct = Math.min(pvD, dayUse) * C.MATCH;
        months.push({ days: d, pvD: pvD, useD: useD, direct: direct, surplus: Math.max(0, pvD - direct),
                      nightNeed: useD * (1 - profile.dayShare) + Math.max(0, dayUse - direct) * 0.5 });
      }
      return months;
    }
    var totalUse = inp.usageKwh;
    if (inp.usageType === 'afname' && pv > 0) {
      // jaarafrekening toont alleen afname van het net: tel het direct gebruikte deel van de zon erbij op
      var guess = inp.usageKwh;
      for (var i = 0; i < 4; i++) {
        var ms = directFor(guess);
        var directYear = ms.reduce(function (a, x) { return a + x.direct * x.days; }, 0);
        guess = inp.usageKwh + directYear;
      }
      totalUse = guess;
    }
    var months = directFor(totalUse);
    var surplusYear = months.reduce(function (a, x) { return a + x.surplus * x.days; }, 0);
    if (inp.exportKwh != null && surplusYear > 0) {
      // eigen teruglevering bekend: schaal het overschot daarop
      var f = inp.exportKwh / surplusYear;
      months.forEach(function (x) { x.surplus = x.surplus * f; });
      surplusYear = inp.exportKwh;
    } else if (inp.exportKwh != null && surplusYear === 0 && inp.exportKwh > 0) {
      months.forEach(function (x, m) { x.surplus = inp.exportKwh * PV_MONTH[m] / x.days; });
      surplusYear = inp.exportKwh;
    }
    return { totalUse: totalUse, months: months, surplusYear: surplusYear };
  }

  /* Opbrengst voor één configuratie (kWh nominaal, aantal sets) in één scenario */
  function scenario(inp, hh, kwh, sets, sc) {
    var t = TARIFFS[inp.contract];
    var eta = sc === 'low' ? C.ETA_LOW : C.ETA_HIGH;
    var util = sc === 'low' ? C.UTIL_LOW : C.UTIL_HIGH;
    var cap = kwh * C.USABLE * C.DEGRADATION;
    var chargeLim = sets * C.SET_KW * (sc === 'low' ? C.SUN_HOURS_LOW : C.SUN_HOURS_HIGH);
    var dischargeLim = sets * C.SET_KW * C.DISCHARGE_HOURS;
    var sellNet = inp.sell - inp.feedCost;
    var solarStored = 0, solarDelivered = 0, gridStored = 0, gridDelivered = 0, solarValue = 0, dynValue = 0;
    var dynDays = sc === 'low' ? C.DYN_DAYS_LOW : C.DYN_DAYS_HIGH;
    var spread = t.spreadLow != null ? (sc === 'low' ? t.spreadLow : t.spreadHigh) : 0;
    for (var m = 0; m < 12; m++) {
      var x = hh.months[m];
      var need = Math.min(x.nightNeed, dischargeLim);
      var stored = Math.min(x.surplus, cap, need / eta, chargeLim) * util;
      var delivered = stored * eta;
      solarStored += stored * x.days;
      solarDelivered += delivered * x.days;
      solarValue += x.days * (delivered * inp.buy - stored * sellNet);
      if (inp.contract === 'dynamisch') {
        var spare = Math.max(0, Math.min(cap, need / eta) - stored);
        var dDays = x.days * dynDays / 365;
        var pLow = t.low, pHigh = t.low + spread;
        gridStored += spare * dDays;
        gridDelivered += spare * eta * dDays;
        dynValue += dDays * spare * (eta * pHigh - pLow);
      }
    }
    var standbyKwh = sets * C.STANDBY_W * 8.76;
    var standbyCost = standbyKwh * inp.buy;
    var yearly = solarValue + Math.max(0, dynValue) - standbyCost;
    return { yearly: yearly, solarValue: solarValue, dynValue: Math.max(0, dynValue), standbyCost: standbyCost,
             solarDelivered: solarDelivered, gridDelivered: gridDelivered, standbyKwh: standbyKwh };
  }

  function configs(prices) {
    prices = prices || DEFAULT_PRICES;
    var list = [6, 9, 12, 15, 18].map(function (k) {
      return { key: String(k), kwh: k, sets: 1, price: prices[k] || DEFAULT_PRICES[k], name: 'Halo ' + k, watt: 3000 };
    });
    var p18 = prices[18] || DEFAULT_PRICES[18];
    list.push({ key: '2x18', kwh: 36, sets: 2, price: 2 * p18, name: '2× Halo 18', watt: 6000 });
    list.push({ key: '3x18', kwh: 54, sets: 3, price: 3 * p18, name: '3× Halo 18', watt: 9000 });
    return list;
  }

  function evaluate(inp, hh, cfg) {
    var lo = scenario(inp, hh, cfg.kwh, cfg.sets, 'low');
    var hi = scenario(inp, hh, cfg.kwh, cfg.sets, 'high');
    var mid = (lo.yearly + hi.yearly) / 2;
    var paybackFast = hi.yearly > 0 ? cfg.price / hi.yearly : null;
    var paybackSlow = lo.yearly > 0 ? cfg.price / lo.yearly : null;
    return {
      key: cfg.key, name: cfg.name, kwh: cfg.kwh, sets: cfg.sets, watt: cfg.watt, price: cfg.price,
      pricePerKwh: cfg.price / cfg.kwh, usableKwh: cfg.kwh * C.USABLE,
      yearlyLow: lo.yearly, yearlyHigh: hi.yearly, yearlyMid: mid,
      paybackFast: paybackFast, paybackSlow: paybackSlow,
      net10Low: lo.yearly * C.HORIZON - cfg.price, net10High: hi.yearly * C.HORIZON - cfg.price,
      detail: { low: lo, high: hi }
    };
  }

  /*
   * Hoofdfunctie. raw = { usageKwh, usageType('afname'|'totaal'), panels, pvKwh?, exportKwh?, profile, contract, buy?, sell?, feedCost? }
   * opts = { prices: {6:..,9:..}, minKwh?: kleinste maat (bv. om vermogen/noodstroom), forceKey? }
   */
  function compute(raw, opts) {
    opts = opts || {};
    var n = normalise(raw);
    var inp = n.input;
    var hh = household(inp);
    var all = configs(opts.prices).map(function (cfg) { return evaluate(inp, hh, cfg); });
    // Meerdere torens alleen overwegen bij hoog verbruik
    var candidates = all.filter(function (e) { return e.sets === 1 || hh.totalUse > 9000; });
    if (opts.minKwh) candidates = candidates.filter(function (e) { return e.kwh >= opts.minKwh || e.key === '18'; });
    var chosen = candidates[0];
    for (var i = 1; i < candidates.length; i++) {
      var next = candidates[i];
      var dSave = next.yearlyMid - chosen.yearlyMid;
      var dPrice = next.price - chosen.price;
      if (dSave > 15 && dPrice / dSave <= C.MARGINAL_PAYBACK_MAX) chosen = next; else break;
    }
    if (opts.forceKey) { all.forEach(function (e) { if (e.key === opts.forceKey) chosen = e; }); }
    var idx = all.indexOf(chosen);
    var smaller = idx > 0 ? all[idx - 1] : null;
    var larger = idx < all.length - 1 && (all[idx + 1].sets === 1 || hh.totalUse > 9000) ? all[idx + 1] : null;
    function delta(a, b) {
      if (!a || !b) return null;
      return { extraPrice: b.price - a.price, extraLow: b.yearlyLow - a.yearlyLow, extraHigh: b.yearlyHigh - a.yearlyHigh };
    }
    var pMid = chosen.yearlyMid > 0 ? chosen.price / chosen.yearlyMid : null;
    var verdict;
    if (chosen.yearlyHigh <= 0 || pMid === null || pMid > 15) verdict = 'nietnu';
    else if (pMid > 10) verdict = 'krap';
    else verdict = 'past';
    var factors = [];
    if (inp.panels === 0 && inp.pvKwh === 0) factors.push('Zonder zonnepanelen komt de besparing alleen uit goedkoop laden met een dynamisch contract.');
    if (hh.surplusYear > 0) factors.push('Je zonoverschot van ± ' + Math.round(hh.surplusYear).toLocaleString('nl-NL') + ' kWh per jaar' + (inp.exportKwh != null ? ' (jouw teruglevering)' : ' (schatting)') + ' is de grootste factor.');
    factors.push('Je stroomgebruik na zonsondergang bepaalt hoeveel opgeslagen stroom je echt gebruikt.');
    if (inp.contract === 'dynamisch') factors.push('Met een dynamisch contract telt het prijsverschil tussen goedkope en dure uren mee.');
    else factors.push('Met een ' + TARIFFS[inp.contract].label + ' contract rekenen we alleen met je eigen zonnestroom.');
    return {
      version: VERSION, peildatum: PEILDATUM, input: inp, warnings: n.warnings,
      household: { totalUse: hh.totalUse, pvKwh: inp.pvKwh, surplusYear: hh.surplusYear },
      recommended: chosen, smaller: smaller, larger: larger,
      toSmaller: delta(smaller, chosen), toLarger: delta(chosen, larger),
      verdict: verdict, factors: factors, all: all
    };
  }

  /* okt 2026: interne bouwstenen beschikbaar voor het vier-batterijenmodel (assets/sr-vier-model.js).
     compute() hierboven blijft ongewijzigd (adviesdocument en Python-port). */
  var api = { compute: compute, VERSION: VERSION, PEILDATUM: PEILDATUM, CONSTANTS: C, TARIFFS: TARIFFS, PROFILES: PROFILES,
              normalise: normalise, household: household, PV_MONTH: PV_MONTH, USE_MONTH: USE_MONTH };
  if (typeof module !== 'undefined' && module.exports) module.exports = api; else root.SRBatterijModel = api;
})(typeof window !== 'undefined' ? window : this);